import { useEffect, useState } from 'react';
import {
  updateUserColor,
  updateUserHideColors,
  updateUserDateFormat,
  updateUserProfile,
  changeUserPassword,
  getNotifications,
} from '../../api';
import { NotificationItem } from '../../types';
import { DateFormat, DateFormatStyle } from '../../utils/DateFormat';
import { getApiErrorMessage } from '../../utils/apiError';
import { useAuth } from '../../hooks/useAuth';
import { useFeedback } from '../../hooks/useFeedback';
import ModalPortal from '../modal/ModalPortal';
import './ProfileSettingsModal.css';

const PRESET_COLORS = [
  '#e74c3c',
  '#3498db',
  '#2ecc71',
  '#f39c12',
  '#9b59b6',
  '#1abc9c',
  '#e67e22',
  '#34495e',
];

const SAMPLE_DATE = '2026-07-15';
const FALLBACK_COLOR = '#3498db';

type Props = {
  userId: number;
  isAdmin?: boolean;
  initialName: string;
  initialLogin: string;
  initialColor: string | null;
  initialHideColors: boolean;
  initialDateFormat: DateFormatStyle;
  initialTab?: 'general' | 'notifications' | 'password';
  onClose: () => void;
  onColorChange: (color: string | null) => void;
  onHideColorsChange: (hide: boolean) => void;
  onDateFormatChange: (format: DateFormatStyle) => void;
  onProfileChange?: (profile: { name: string; login: string }) => void;
};

const checkStatusLabel = (
  item: NotificationItem,
  dateFormat: DateFormatStyle
) => {
  const neuter = item.type === 'slg';
  const feminine = item.type === 'insurance';
  if (item.status === 'overdue' || item.overdue) {
    return feminine ? 'Просрочена' : neuter ? 'Просрочено' : 'Просрочен';
  }
  if (item.status === 'soon') {
    return item.days_left != null
      ? `Истекает через ${item.days_left} дн.`
      : 'Истекает скоро';
  }
  if (item.status === 'missing' || !item.next_date) {
    if (item.needs_birth_date) return 'Нужна дата рождения';
    return feminine ? 'Не указана' : neuter ? 'Не указано' : 'Не указан';
  }
  return `До ${DateFormat(item.next_date, dateFormat)}`;
};

const ProfileSettingsModal = ({
  userId,
  isAdmin = false,
  initialName,
  initialLogin,
  initialColor,
  initialHideColors,
  initialDateFormat,
  initialTab,
  onClose,
  onColorChange,
  onHideColorsChange,
  onDateFormatChange,
  onProfileChange,
}: Props) => {
  const { patchUser } = useAuth();
  const { showError, showSuccess, feedbackNode } = useFeedback();
  const [color, setColor] = useState(
    initialColor && /^#[0-9a-f]{6}$/i.test(initialColor)
      ? initialColor.toLowerCase()
      : FALLBACK_COLOR
  );
  const [hideColors, setHideColors] = useState(initialHideColors);
  const [dateFormat, setDateFormat] = useState<DateFormatStyle>(initialDateFormat);
  const [profileForm, setProfileForm] = useState({
    name: initialName,
    login: initialLogin,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingColor, setSavingColor] = useState(false);
  const [savingFormat, setSavingFormat] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    next: '',
    confirm: '',
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [activeTab, setActiveTab] = useState<'general' | 'notifications' | 'password'>(
    initialTab || (isAdmin ? 'notifications' : 'general')
  );
  const [peerAlerts, setPeerAlerts] = useState<NotificationItem[]>([]);
  const [adminSummary, setAdminSummary] = useState<NotificationItem[]>([]);
  const [adminSlgSummary, setAdminSlgSummary] = useState<NotificationItem[]>([]);
  const [adminInsuranceSummary, setAdminInsuranceSummary] = useState<
    NotificationItem[]
  >([]);
  const [warnDays, setWarnDays] = useState(14);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  useEffect(() => {
    if (initialColor && /^#[0-9a-f]{6}$/i.test(initialColor)) {
      setColor(initialColor.toLowerCase());
    }
    setHideColors(initialHideColors);
    setDateFormat(initialDateFormat);
  }, [initialColor, initialHideColors, initialDateFormat]);

  useEffect(() => {
    setProfileForm({ name: initialName, login: initialLogin });
  }, [initialName, initialLogin]);

  useEffect(() => {
    const loadNotifications = async () => {
      setLoadingNotifications(true);
      try {
        const res = await getNotifications();
        setPeerAlerts(res.data.items || []);
        setAdminSummary(res.data.admin_vlek_summary || []);
        setAdminSlgSummary(res.data.admin_slg_summary || []);
        setAdminInsuranceSummary(res.data.admin_insurance_summary || []);
        setWarnDays(res.data.warn_days || 14);
      } catch (error) {
        showError(getApiErrorMessage(error, 'Ошибка загрузки уведомлений'));
      }
      setLoadingNotifications(false);
    };
    void loadNotifications();
  }, [userId, showError]);

  const handleSaveColor = async (nextColor?: string) => {
    const value = (nextColor || color).toLowerCase();
    if (!/^#[0-9a-f]{6}$/i.test(value)) {
      showError('Цвет должен быть в формате #RRGGBB');
      return;
    }
    setSavingColor(true);
    try {
      const res = await updateUserColor(userId, value);
      setColor(res.data.color || value);
      setHideColors(false);
      patchUser({ color: res.data.color, hide_colors: false });
      onColorChange(res.data.color);
      onHideColorsChange(false);
    } catch {
      showError('Ошибка сохранения цвета');
    }
    setSavingColor(false);
  };

  const handleHideColors = async () => {
    setSavingColor(true);
    try {
      const res = await updateUserHideColors(userId, true);
      setHideColors(true);
      patchUser({
        color: res.data.color,
        hide_colors: true,
      });
      onHideColorsChange(true);
    } catch {
      showError('Ошибка сохранения настройки');
    }
    setSavingColor(false);
  };

  const handleSaveDateFormat = async (next: DateFormatStyle) => {
    setSavingFormat(true);
    try {
      const res = await updateUserDateFormat(userId, next);
      setDateFormat(res.data.date_format);
      patchUser({ date_format: res.data.date_format });
      onDateFormatChange(res.data.date_format);
    } catch {
      showError('Ошибка сохранения формата даты');
    }
    setSavingFormat(false);
  };

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = profileForm.name.trim();
    const login = profileForm.login.trim();
    if (!name) {
      showError('Укажите имя');
      return;
    }
    if (!login) {
      showError('Укажите логин');
      return;
    }
    if (
      name === initialName.trim() &&
      login === initialLogin.trim()
    ) {
      showSuccess('Изменений нет');
      return;
    }
    setSavingProfile(true);
    try {
      const res = await updateUserProfile(userId, { name, login });
      setProfileForm({ name: res.data.name, login: res.data.login });
      patchUser({ name: res.data.name, login: res.data.login });
      onProfileChange?.({ name: res.data.name, login: res.data.login });
      showSuccess('Имя и логин сохранены');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Не удалось сохранить профиль'));
    }
    setSavingProfile(false);
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordForm.next !== passwordForm.confirm) {
      showError('Новый пароль и подтверждение не совпадают');
      return;
    }
    if (passwordForm.next.length < 4) {
      showError('Новый пароль должен быть не короче 4 символов');
      return;
    }
    setSavingPassword(true);
    try {
      await changeUserPassword(userId, passwordForm.current, passwordForm.next);
      setPasswordForm({ current: '', next: '', confirm: '' });
      showSuccess('Пароль изменён');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Ошибка смены пароля'));
    }
    setSavingPassword(false);
  };

  return (
    <ModalPortal>
    <>
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal profile-settings-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <h3>Настройки</h3>

        <div className="settings-tabs" role="tablist" aria-label="Разделы настроек">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'general'}
            className={`settings-tab${activeTab === 'general' ? ' active' : ''}`}
            onClick={() => setActiveTab('general')}
          >
            Общие
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'notifications'}
            className={`settings-tab${activeTab === 'notifications' ? ' active' : ''}`}
            onClick={() => setActiveTab('notifications')}
          >
            Уведомления
            {peerAlerts.length > 0 ? (
              <span className="settings-tab-badge">{peerAlerts.length}</span>
            ) : null}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'password'}
            className={`settings-tab${activeTab === 'password' ? ' active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            Пароль
          </button>
        </div>

        <div className="modal-body">
          {activeTab === 'general' && (
            <>
              <div className="settings-block">
                <h4>Имя и логин</h4>
                <p className="settings-hint">
                  Логин нужен для входа. После смены входите с новым логином.
                </p>
                <form
                  className="settings-password-form"
                  onSubmit={handleSaveProfile}
                >
                  <label>
                    Имя
                    <input
                      type="text"
                      value={profileForm.name}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          name: e.target.value,
                        })
                      }
                      maxLength={100}
                      required
                      autoComplete="name"
                    />
                  </label>
                  <label>
                    Логин
                    <input
                      type="text"
                      value={profileForm.login}
                      onChange={(e) =>
                        setProfileForm({
                          ...profileForm,
                          login: e.target.value,
                        })
                      }
                      maxLength={50}
                      required
                      autoComplete="username"
                    />
                  </label>
                  <button
                    type="submit"
                    className="btn-save"
                    disabled={savingProfile}
                  >
                    {savingProfile ? 'Сохранение…' : 'Сохранить'}
                  </button>
                </form>
              </div>

              <div className="settings-block">
                <h4>Выбор цвета отображения записей пилота</h4>
                <p className="settings-hint">
                  Значок ∅ скрывает цвета всех пилотов только у вас. Выбор цвета снова
                  включает подсветку.
                </p>
                <div className="settings-color-presets">
                  <button
                    type="button"
                    className={`settings-color-swatch settings-color-none${hideColors ? ' active' : ''}`}
                    onClick={handleHideColors}
                    disabled={savingColor}
                    aria-label="Скрыть цвета"
                    title="Скрыть цвета"
                  >
                    ∅
                  </button>
                  {PRESET_COLORS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      className={`settings-color-swatch${!hideColors && color === c ? ' active' : ''}`}
                      style={{ backgroundColor: c }}
                      onClick={() => {
                        setColor(c);
                        handleSaveColor(c);
                      }}
                      disabled={savingColor}
                      aria-label={`Выбрать цвет ${c}`}
                    />
                  ))}
                </div>
                <div className="settings-color-row">
                  <input
                    type="color"
                    value={color}
                    onChange={(e) => {
                      setColor(e.target.value);
                      void handleSaveColor(e.target.value);
                    }}
                    disabled={savingColor}
                    aria-label="Выбор цвета"
                  />
                  <span className="settings-color-hex">
                    {hideColors ? 'Цвета скрыты' : color.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="settings-block">
                <h4>Формат даты</h4>
                <p className="settings-hint">Как показывать даты во всём приложении</p>
                <div className="settings-format-options">
                  <button
                    type="button"
                    className={`settings-format-option${dateFormat === 'dot' ? ' active' : ''}`}
                    disabled={savingFormat}
                    onClick={() => handleSaveDateFormat('dot')}
                  >
                    <strong>Через точку</strong>
                    <span>{DateFormat(SAMPLE_DATE, 'dot')}</span>
                  </button>
                  <button
                    type="button"
                    className={`settings-format-option${dateFormat === 'long' ? ' active' : ''}`}
                    disabled={savingFormat}
                    onClick={() => handleSaveDateFormat('long')}
                  >
                    <strong>Текстом</strong>
                    <span>{DateFormat(SAMPLE_DATE, 'long')}</span>
                  </button>
                </div>
              </div>
            </>
          )}

          {activeTab === 'notifications' && (
            <div className="settings-block">
              <h4>Уведомления</h4>
              <p className="settings-hint">
                Истечение ВЛЭК у участников, СЛГ и страховки у бортов клуба — за{' '}
                {warnDays} дней и менее; дни рождения участников — в день
                праздника
              </p>
              {loadingNotifications ? (
                <p className="settings-notifications-empty">Загрузка...</p>
              ) : peerAlerts.length === 0 ? (
                <p className="settings-notifications-empty">
                  Сейчас нет срочных уведомлений
                </p>
              ) : (
                <ul className="settings-notifications-list">
                  {peerAlerts.map((item) => (
                    <li
                      key={`${item.type}-${item.user_id ?? item.aircraft_id}`}
                      className={`settings-notification-item${
                        item.overdue || item.severity === 'critical'
                          ? ' critical'
                          : ' warning'
                      }`}
                    >
                      <strong>{item.user_name}</strong>
                      <span>
                        {item.message ||
                          (item.type === 'birthday'
                            ? `Сегодня день рождения у ${item.user_name}`
                            : item.type === 'slg'
                              ? item.overdue
                                ? 'СЛГ просрочено'
                                : `СЛГ истекает через ${item.days_left} дн.`
                              : item.type === 'insurance'
                                ? item.overdue
                                  ? 'Страховка просрочена'
                                  : `Страховка истекает через ${item.days_left} дн.`
                                : item.overdue
                                  ? 'ВЛЭК просрочен'
                                  : `ВЛЭК истекает через ${item.days_left} дн.`)}
                      </span>
                      {item.next_date && (
                        <span className="settings-notification-date">
                          Срок: {DateFormat(item.next_date, dateFormat)}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}

              {isAdmin && (
                <>
                  <div className="settings-admin-summary">
                    <h5>Сводка ВЛЭК (участники клуба)</h5>
                    {adminSummary.length === 0 ? (
                      <p className="settings-notifications-empty">Нет участников</p>
                    ) : (
                      <ul className="settings-notifications-list admin">
                        {adminSummary.map((item) => (
                          <li
                            key={`admin-vlek-${item.user_id}`}
                            className={`settings-notification-item status-${
                              item.status || 'ok'
                            }`}
                          >
                            <strong>{item.user_name}</strong>
                            <span>{checkStatusLabel(item, dateFormat)}</span>
                            {item.next_date && item.status !== 'missing' && (
                              <span className="settings-notification-date">
                                {DateFormat(item.next_date, dateFormat)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="settings-admin-summary">
                    <h5>Сводка СЛГ (борты клуба)</h5>
                    {adminSlgSummary.length === 0 ? (
                      <p className="settings-notifications-empty">Нет бортов</p>
                    ) : (
                      <ul className="settings-notifications-list admin">
                        {adminSlgSummary.map((item) => (
                          <li
                            key={`admin-slg-${item.aircraft_id}`}
                            className={`settings-notification-item status-${
                              item.status || 'ok'
                            }`}
                          >
                            <strong>{item.user_name}</strong>
                            <span>{checkStatusLabel(item, dateFormat)}</span>
                            {item.next_date && item.status !== 'missing' && (
                              <span className="settings-notification-date">
                                {DateFormat(item.next_date, dateFormat)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="settings-admin-summary">
                    <h5>Сводка страховки (борты клуба)</h5>
                    {adminInsuranceSummary.length === 0 ? (
                      <p className="settings-notifications-empty">Нет бортов</p>
                    ) : (
                      <ul className="settings-notifications-list admin">
                        {adminInsuranceSummary.map((item) => (
                          <li
                            key={`admin-ins-${item.aircraft_id}`}
                            className={`settings-notification-item status-${
                              item.status || 'ok'
                            }`}
                          >
                            <strong>{item.user_name}</strong>
                            <span>{checkStatusLabel(item, dateFormat)}</span>
                            {item.next_date && item.status !== 'missing' && (
                              <span className="settings-notification-date">
                                {DateFormat(item.next_date, dateFormat)}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {activeTab === 'password' && (
            <div className="settings-block">
              <h4>Смена пароля</h4>
              <form className="settings-password-form" onSubmit={handleChangePassword}>
                <label>
                  Текущий пароль
                  <input
                    type="password"
                    value={passwordForm.current}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, current: e.target.value })
                    }
                    required
                    autoComplete="current-password"
                  />
                </label>
                <label>
                  Новый пароль
                  <input
                    type="password"
                    value={passwordForm.next}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, next: e.target.value })
                    }
                    required
                    minLength={4}
                    autoComplete="new-password"
                  />
                </label>
                <label>
                  Подтверждение
                  <input
                    type="password"
                    value={passwordForm.confirm}
                    onChange={(e) =>
                      setPasswordForm({ ...passwordForm, confirm: e.target.value })
                    }
                    required
                    minLength={4}
                    autoComplete="new-password"
                  />
                </label>
                <button type="submit" className="btn-save" disabled={savingPassword}>
                  {savingPassword ? 'Сохранение...' : 'Сменить пароль'}
                </button>
              </form>
            </div>
          )}
        </div>
        <div className="modal-actions">
          <button type="button" className="btn-cancel" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
    {feedbackNode}
    </>
    </ModalPortal>
  );
};

export default ProfileSettingsModal;
